import React, { useMemo } from "react";

import { getEnvironmentalContextSnapshot } from "../../../utils/environmentalContextSummary";
import { getEnvironmentalMetricValueClass } from "../../../utils/environmentalMetricStyles";

const UNIT_LABELS = Object.freeze({
  CELSIUS: "deg C",
  FAHRENHEIT: "deg F",
  MILLIBARS: "mb",
  MICROGRAMS_PER_CUBIC_METER: "ug/m3",
  PARTS_PER_BILLION: "ppb",
});
const PENDING_POLLUTANT_ITEMS = Object.freeze([
  { variable: "p25", label: "PM2.5" },
  { variable: "pm10", label: "PM10" },
  { variable: "no2", label: "NO2" },
  { variable: "o3", label: "O3" },
  { variable: "so2", label: "SO2" },
  { variable: "co", label: "CO" },
]);

function roundNumericValue(value, decimals = 0) {
  if (typeof value !== "number") {
    return value;
  }

  const multiplier = 10 ** decimals;
  return Math.round(value * multiplier) / multiplier;
}

function DefaultSummaryCard({ label, value, unit, valueClassName = "text-typography" }) {
  return (
    <div className="card h-full px-3 py-2">
      <p className="text-sm text-greyscale-3">{label}</p>
      <div className="flex items-end gap-2">
        <p className={`text-2xl font-semibold ${valueClassName}`}>{value}</p>
        {unit ? <p className="pb-1 text-sm text-greyscale-3">{unit}</p> : null}
      </div>
    </div>
  );
}

function normalizeVariableName(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : null;
}

function renderCalculatingValue(t) {
  return (
    <span className="animate-pulse text-neutral-400">
      {t("profilePage.metricCalculating", "Calculating…")}
    </span>
  );
}

function formatObservationValue(metricKey, observation) {
  if (!observation || observation.value === null || observation.value === undefined) {
    return null;
  }

  const unit = String(observation.unit || "").trim().toUpperCase();
  const normalizedUnit = UNIT_LABELS[unit] || observation.unit || null;

  switch (metricKey) {
    case "temperature":
      return {
        value: String(roundNumericValue(observation.value, 0)),
        unit: unit === "FAHRENHEIT" ? "°F" : "°C",
      };
    case "humidity":
      return {
        value: String(roundNumericValue(observation.value, 0)),
        unit: "%",
      };
    case "pressure":
      return {
        value: String(roundNumericValue(observation.value, 0)),
        unit: normalizedUnit,
      };
    case "aqi":
    case "pollen":
      return {
        value: String(roundNumericValue(observation.value, 0)),
        unit: null,
      };
    default:
      return {
        value: String(roundNumericValue(observation.value, 1)),
        unit: normalizedUnit,
      };
  }
}

export default function EnvironmentalContextSection({
  context,
  t,
  CardComponent = DefaultSummaryCard,
  showPendingPlaceholders = false,
}) {
  const snapshot = useMemo(
    () => getEnvironmentalContextSnapshot(context),
    [context],
  );

  const metricItems = useMemo(
    () => {
      const baseItems = [
        {
          key: "aqi",
          metricKey: "aqi",
          label: t("profilePage.environmentAqiLabel", "AQI"),
          observation: snapshot?.metrics?.aqi || null,
          formattedValue: formatObservationValue("aqi", snapshot?.metrics?.aqi || null),
        },
        {
          key: "temperature",
          metricKey: "temperature",
          label: t("profilePage.environmentTemperatureLabel", "Temp"),
          observation: snapshot?.metrics?.temperature || null,
          formattedValue: formatObservationValue(
            "temperature",
            snapshot?.metrics?.temperature || null,
          ),
        },
        {
          key: "humidity",
          metricKey: "humidity",
          label: t("profilePage.environmentHumidityLabel", "Humidity"),
          observation: snapshot?.metrics?.humidity || null,
          formattedValue: formatObservationValue(
            "humidity",
            snapshot?.metrics?.humidity || null,
          ),
        },
        {
          key: "pollen",
          metricKey: "pollen",
          label: t("profilePage.environmentPollenLabel", "Pollen"),
          observation: snapshot?.metrics?.pollen || null,
          formattedValue: formatObservationValue("pollen", snapshot?.metrics?.pollen || null),
        },
        {
          key: "pressure",
          metricKey: "pressure",
          label: t("profilePage.environmentPressureLabel", "Pressure"),
          observation: snapshot?.metrics?.pressure || null,
          formattedValue: formatObservationValue(
            "pressure",
            snapshot?.metrics?.pressure || null,
          ),
        },
      ];

      const resolvedBaseItems = baseItems
        .map((item) => {
          if (item.observation && item.formattedValue) {
            return item;
          }

          if (!showPendingPlaceholders) {
            return null;
          }

          return {
            ...item,
            pending: true,
          };
        })
        .filter(Boolean);

      const pollutantObservations = Array.isArray(snapshot?.pollutants)
        ? snapshot.pollutants
        : [];
      const pollutantByVariable = new Map();
      pollutantObservations.forEach((observation) => {
        const variable = normalizeVariableName(observation?.variable);
        if (!variable || pollutantByVariable.has(variable)) {
          return;
        }

        pollutantByVariable.set(variable, observation);
      });
      const standardPollutantVariables = new Set(
        PENDING_POLLUTANT_ITEMS.map((item) => item.variable),
      );
      const standardPollutantCards = PENDING_POLLUTANT_ITEMS.map((item) => {
        const observation =
          pollutantByVariable.get(item.variable) ||
          (item.variable === "p25" ? pollutantByVariable.get("pm25") : null);

        if (observation) {
          return {
            key: `pollutant-${observation.variable || item.variable}`,
            metricKey: "pollutant",
            label: observation.label || item.label,
            observation,
            formattedValue: formatObservationValue("pollutant", observation),
          };
        }

        if (!showPendingPlaceholders) {
          return null;
        }

        return {
          key: `pollutant-pending-${item.variable}`,
          metricKey: "pollutant",
          label: item.label,
          observation: null,
          formattedValue: null,
          pending: true,
        };
      }).filter((item) => item && (item.pending || item.formattedValue));

      const additionalPollutantCards = pollutantObservations
        .filter((observation) => {
          const variable = normalizeVariableName(observation?.variable);
          if (!variable) {
            return true;
          }

          if (variable === "pm25") {
            return false;
          }

          return !standardPollutantVariables.has(variable);
        })
        .map((observation) => ({
          key: `pollutant-${observation.variable || observation.label || "unknown"}`,
          metricKey: "pollutant",
          label: observation.label || observation.variable || "Pollutant",
          observation,
          formattedValue: formatObservationValue("pollutant", observation),
        }))
        .filter((item) => item.formattedValue);

      return [
        ...resolvedBaseItems,
        ...standardPollutantCards,
        ...additionalPollutantCards,
      ];
    },
    [showPendingPlaceholders, snapshot, t],
  );

  if (!snapshot && !showPendingPlaceholders) {
    return null;
  }

  return (
    <>
      {metricItems.length ? (
        metricItems.map((item) => (
          <CardComponent
            key={item.key}
            label={item.label}
            value={
              item.pending ? renderCalculatingValue(t) : item.formattedValue.value
            }
            unit={item.pending ? undefined : item.formattedValue.unit}
            valueClassName={
              item.pending
                ? "animate-pulse text-neutral-400"
                : getEnvironmentalMetricValueClass(
                    item.metricKey,
                    item.observation,
                  )
            }
          />
        ))
      ) : (
        <div className="col-span-full">
          <p className="text-sm text-typography">
            {snapshot.summary ||
              t("profilePage.environmentContextAvailable", "Context available")}
          </p>
        </div>
      )}
    </>
  );
}
